/**
 * In lib rather than the app because the same links are built in two places
 * that can't import each other's copy: the notification feed in the browser,
 * and the mention emails the backend sends. `routes.tsx` consumes these
 * segments too — a route table and a path builder that disagree produce links
 * that 404 without either side changing.
 */

export const VERSION_SEGMENT = "versions";
export const CHANNEL_SIZE_SEGMENT = "sizes";
/** Names a resize by its own `ad_brief_assets` id rather than by channel size. */
export const RESIZE_SEGMENT = "resizes";

/** The AMP board's projects list. A project's page sits at its own `/:projectId`, and a task opened from it at `/:projectId/tasks/:taskId`. */
export const PROJECTS_AREA_SEGMENT = "projects";
/** The AMP board's tasks, a top-level page beside the projects list, with each task at `/:taskId`. */
export const PROJECTS_TASKS_SEGMENT = "tasks";
/** The AMP board's library of files and canvases, a top-level page beside the projects list. */
export const PROJECTS_LIBRARY_SEGMENT = "library";

/** A canvas in the AMP board's library. Root-relative, as {@link buildCommentThreadPath} is. */
export function buildCanvasPath(canvasId: string): string {
  return `/${PROJECTS_LIBRARY_SEGMENT}/canvases/${canvasId}`;
}

/** A file open in the AMP board's library. Root-relative, as {@link buildCommentThreadPath} is. */
export function buildLibraryFilePath(fileId: string): string {
  return `/${PROJECTS_LIBRARY_SEGMENT}/${encodeURIComponent(fileId)}`;
}

/** A canvas under the projects area, which redirects to its library page: a link the API hands out while it is ahead of the app still opens. */
export function buildProjectsAreaCanvasPath(canvasId: string): string {
  return `/${PROJECTS_AREA_SEGMENT}${buildCanvasPath(canvasId)}`;
}

/**
 * Opens a comment once the ad, project, task or file is open: its thread
 * selected and the comment highlighted. A thread's id works too, and opens the
 * thread with nothing highlighted, as links sent before comments had their own
 * do.
 */
export const COMMENT_PARAM = "comment";

/**
 * Turns the rail's "show resolved" filter on, for a link to a thread that is
 * already settled.
 *
 * In the link rather than in router state because the same URL is pasted,
 * bookmarked and reloaded, and state survives none of those — a link that only
 * worked on the first click would be the bug this param exists to fix.
 */
export const COMMENT_SHOW_RESOLVED_PARAM = "showResolved";

/** The list surface the ad opens over; both host the same concept routes. */
export type AdConceptParent =
  | { kind: "folder"; folderId: string }
  | { kind: "project"; projectId: string };

/**
 * A filed ad opens in its folder, where the team reviewing it works; the chat
 * that generated it is the fallback. Null when the ad has neither.
 */
export function adConceptParent({
  folderId,
  projectId,
}: {
  folderId: string | null;
  projectId: string | null;
}): AdConceptParent | null {
  if (folderId !== null) {
    return { kind: "folder", folderId };
  }

  return projectId === null ? null : { kind: "project", projectId };
}

export type AdConceptPath = {
  parent: AdConceptParent;
  adBriefId: string;
  assetId: string;
  /** Null for the primary render, which has no channel of its own. */
  channelSizeId: string | null;
};

/**
 * The channel size scopes the thread list, so a link without it would open the
 * ad and find nothing to select.
 *
 * Root-relative because the app's `navigate` injects the workspace slug itself;
 * for a link leaving the process, pass it through {@link absoluteAdStudioUrl}.
 */
export function buildCommentThreadPath({
  parent,
  adBriefId,
  assetId,
  channelSizeId,
  resizeAssetId,
  commentId,
  isThreadResolved = false,
}: AdConceptPath & {
  /** The comment to open, or a thread's id to open the thread with nothing highlighted. */
  commentId: string;
  /** A resize of `assetId` to open by its own id, since a channel size can hold more than one. */
  resizeAssetId?: string | null;
  /**
   * Whether the thread is settled as of the link being built. Resolved threads
   * are filtered out of the rail, so the link has to ask for them back.
   */
  isThreadResolved?: boolean;
}): string {
  const parentPath =
    parent.kind === "folder"
      ? `/ad-studio/folders/${parent.folderId}`
      : `/ad-studio/projects/${parent.projectId}`;
  const detailPath = `${parentPath}/concepts/${adBriefId}/${assetId}`;
  const openPath = resizeAssetId
    ? `${detailPath}/${RESIZE_SEGMENT}/${resizeAssetId}`
    : channelSizeId
      ? `${detailPath}/${CHANNEL_SIZE_SEGMENT}/${channelSizeId}`
      : detailPath;

  return `${openPath}?${commentThreadParams({ commentId, isThreadResolved })}`;
}

export type CommentThreadLink = {
  /** As in {@link buildCommentThreadPath}. */
  commentId: string;
  /** As in {@link buildCommentThreadPath}. */
  isThreadResolved?: boolean;
};

/** A thread on an AMP board project. Root-relative, as {@link buildCommentThreadPath} is. */
export function buildAmpProjectCommentThreadPath({
  projectId,
  commentId,
  isThreadResolved = false,
}: CommentThreadLink & { projectId: string }): string {
  return `/${PROJECTS_AREA_SEGMENT}/${projectId}?${commentThreadParams({ commentId, isThreadResolved })}`;
}

/**
 * A thread on an AMP board task. Root-relative, as {@link buildCommentThreadPath} is.
 * Under the projects area, which redirects to the task's own page: a link sent while the API is ahead of the app still opens.
 */
export function buildAmpTaskCommentThreadPath({
  taskId,
  commentId,
  isThreadResolved = false,
}: CommentThreadLink & { taskId: string }): string {
  return `/${PROJECTS_AREA_SEGMENT}/${PROJECTS_TASKS_SEGMENT}/${taskId}?${commentThreadParams({ commentId, isThreadResolved })}`;
}

/** The query string that opens a comment, without its `?`. */
export function commentThreadParams({
  commentId,
  isThreadResolved = false,
}: CommentThreadLink): string {
  const params = new URLSearchParams({ [COMMENT_PARAM]: commentId });

  if (isThreadResolved) {
    params.set(COMMENT_SHOW_RESOLVED_PARAM, "true");
  }

  return params.toString();
}

export function absoluteAdStudioUrl({
  baseUrl,
  workspaceSlug,
  path,
}: {
  /** App origin, no trailing slash. */
  baseUrl: string;
  workspaceSlug: string;
  /** Root-relative, leading slash. */
  path: string;
}): string {
  return `${baseUrl}/${workspaceSlug}${path}`;
}
